// Shared travel expense schema + helpers used by both admin and client apps

export type TravelFieldType = 'date' | 'string' | 'currency' | 'number';

export interface TravelFieldDef {
  name: string;
  type: TravelFieldType;
  label: string;
  required?: boolean;
  default?: any;
  constraints?: {
    maxLength?: number;
    min?: number;
    max?: number;
    step?: number;
    pattern?: string;
  };
}

export interface TravelSchemaComputeTermField {
  kind: 'field';
  name: string;
}

export interface TravelSchemaComputeTermMileage {
  kind: 'mileage';
  distanceField: string;
  rateRef: 'medical_mileage';
}

export interface TravelSchema {
  version: number;
  fields: TravelFieldDef[];
  compute: {
    type: 'sum';
    terms: Array<TravelSchemaComputeTermField | TravelSchemaComputeTermMileage>;
    target: string; // usually 'amount'
  };
}

export const fetchTravelSchema = async (
  baseUrl: string,
  headers: HeadersInit
): Promise<TravelSchema> => {
  const url = `${baseUrl}/v2/fundrequests/schemas/travel`;
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) {
    throw new Error(`Failed to load travel schema: ${resp.status}`);
  }
  return (await resp.json()) as TravelSchema;
};

export const travelFieldNames = (schema: TravelSchema): string[] =>
  schema.fields.map((f) => f.name);

export const buildTravelDefault = (
  schema: TravelSchema,
  opts?: { withGL?: boolean }
): Record<string, any> => {
  const defaults: Record<string, any> = {};
  for (const f of schema.fields) {
    // If GL code should be omitted in client, skip unless withGL is true
    if (f.name === 'gl_code' && !opts?.withGL) continue;
    defaults[f.name] = f.default !== undefined ? f.default : null;
  }
  return defaults;
};

export const calculateTravelTotal = (
  itemValues: Record<string, any>,
  annualRates: { medical_mileage?: number } | null | undefined,
  schema: TravelSchema
): number => {
  if (!schema?.compute || schema.compute.type !== 'sum') return 0;
  const mileageRate = Number(annualRates?.medical_mileage || 0) || 0;
  let total = 0;
  for (const term of schema.compute.terms) {
    if (term.kind === 'field') {
      const v = Number(itemValues?.[term.name] || 0);
      total += isNaN(v) ? 0 : v;
    } else if (term.kind === 'mileage') {
      const dist = Number(itemValues?.[term.distanceField] || 0);
      total += (isNaN(dist) ? 0 : dist) * mileageRate;
    }
  }
  return total;
};

